import { AxiosResponse } from "axios";
import {
  Estagio,
  EstagioFilters,
  RelatorioEstagio,
  PaginationParams,
  RelatorioPersonalizadoFilters,
  RelatorioPersonalizadoResponse,
  RelatorioEstagioExportResponse,
} from "@/types";
import api from "../httpClient";
import { fetchAll, fetchById, create, update, deleteById, fetchBy } from "../apiHelpers";
import { alunoService, cidadeService, cursoService, empresaService, instituicaoService, seguradoraService } from "./entities";

async function normalizeBlobError(error: any): Promise<void> {
  if (error.response?.data instanceof Blob) {
    const text = await error.response.data.text();
    try {
      error.response.data = JSON.parse(text);
    } catch {
      error.response.data = { error: text };
    }
  }
}

export const estagioService = {
  list: (filters?: EstagioFilters) => fetchAll<Estagio, EstagioFilters>("/estagio", filters),
  getById: (id: number) => fetchById<Estagio>("/estagio", id),
  getByStatus: (status: string) => fetchBy<Estagio>("/estagio", "status", status),
  create: (data: Omit<Estagio, "id">) => create<Estagio>("/estagio", data),
  update: (id: number, data: Partial<Estagio>) => update<Estagio>("/estagio", id, data),
  cancelar: async (id: number, data: { dataCancelamento: string }): Promise<Estagio> => {
    const response: AxiosResponse<Estagio> = await api.patch(`/estagio/${id}/cancelar`, data);
    return response.data;
  },
  reativar: async (id: number): Promise<Estagio> => {
    const response: AxiosResponse<Estagio> = await api.patch(`/estagio/${id}/reativar`);
    return response.data;
  },
  delete: (id: number) => deleteById("/estagio", id),
  generateTerm: async (id: number): Promise<Blob> => {
    try {
      const response = await api.post(`/gerar-termo/${id}/template`, {}, { responseType: "blob" });
      return response.data;
    } catch (error: any) {
      await normalizeBlobError(error);
      throw error;
    }
  },
};

export const relatorioEstagioService = {
  list: (filters?: PaginationParams) => fetchAll<RelatorioEstagio>("/relatorio-estagio", filters),
  getById: (id: number) => fetchById<RelatorioEstagio>("/relatorio-estagio", id),
  getByEstagio: async (estagioId: number): Promise<RelatorioEstagio[]> => {
    try {
      const response = await api.get(`/relatorio-estagio/estagio/${estagioId}`);
      if (response.data && typeof response.data === "object" && "mensagem" in response.data && !Array.isArray(response.data)) {
        return [];
      }
      return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      if (error?.response?.status === 200 && error?.response?.data?.mensagem) {
        return [];
      }
      throw error;
    }
  },
  getProximosVencer: async (dias: number = 10): Promise<RelatorioEstagio[]> => {
    const response = await api.get(`/relatorio-estagio/proximos-vencer?dias=${dias}`);
    return Array.isArray(response.data) ? response.data : [];
  },
  getByAluno: async (alunoId: number): Promise<RelatorioEstagio[]> => {
    try {
      const response = await api.get(`/relatorio-estagio/aluno/${alunoId}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      if (error?.response?.status === 200 && error?.response?.data?.mensagem) {
        return [];
      }
      throw error;
    }
  },
  create: (data: Omit<RelatorioEstagio, "id" | "createdAt" | "updatedAt">) => create<RelatorioEstagio>("/relatorio-estagio", data),
  update: (id: number, data: Partial<RelatorioEstagio>) => update<RelatorioEstagio>("/relatorio-estagio", id, data),
  delete: (id: number) => deleteById("/relatorio-estagio", id),
  personalizado: async (filters: RelatorioPersonalizadoFilters): Promise<RelatorioPersonalizadoResponse> => {
    const response: AxiosResponse<RelatorioPersonalizadoResponse> = await api.post("/relatorio-estagio/personalizado", filters);
    return response.data;
  },
  personalizadoExport: async (filters: RelatorioPersonalizadoFilters): Promise<RelatorioPersonalizadoResponse> => {
    const response: AxiosResponse<RelatorioPersonalizadoResponse> = await api.post("/relatorio-estagio/personalizado/exportar", filters);
    return response.data;
  },
  exportarLista: async (filters: PaginationParams): Promise<RelatorioEstagioExportResponse> => {
    const response: AxiosResponse<RelatorioEstagioExportResponse> = await api.post("/relatorio-estagio/exportar", filters);
    return response.data;
  },
};

export const gerarTermoService = {
  listEstagios: (filters?: EstagioFilters) => fetchAll<Estagio, EstagioFilters>("/estagio", filters),
  gerarTermo: async (estagioId: number): Promise<Blob> => {
    try {
      const response: AxiosResponse<Blob> = await api.get(`/gerar-termo/${estagioId}/download`, { responseType: "blob" });
      return response.data;
    } catch (error: any) {
      await normalizeBlobError(error);
      throw error;
    }
  },
};

export const termoRescisaoService = {
  listEstagios: (filters?: EstagioFilters) => fetchAll<Estagio, EstagioFilters>("/estagio", filters),
  buscarDadosEstagio: async (estagioId: number): Promise<any> => {
    const response = await api.get(`/termo-rescisao/${estagioId}/dados`);
    return response.data;
  },
  gerarTermoRescisao: async (estagioId: number, dataRescisao: string): Promise<Blob> => {
    try {
      const response: AxiosResponse<Blob> = await api.post(`/termo-rescisao/${estagioId}/download`, { dataRescisao }, { responseType: "blob" });
      return response.data;
    } catch (error: any) {
      await normalizeBlobError(error);
      throw error;
    }
  },
};

export const previewTermoPdfService = {
  listEstagios: (filters?: EstagioFilters) => fetchAll<Estagio, EstagioFilters>("/estagio", filters),
  buscarDadosEstagio: async (estagioId: number): Promise<any> => {
    const response = await api.get(`/preview-termo-pdf/${estagioId}/dados`);
    return response.data;
  },
  gerarPreviewPdf: async (estagioId: number): Promise<Blob> => {
    try {
      const response: AxiosResponse<Blob> = await api.get(`/preview-termo-pdf/${estagioId}/download`, { responseType: "blob" });
      return response.data;
    } catch (error: any) {
      await normalizeBlobError(error);
      throw error;
    }
  },
};

export const termoRescisaoPdfService = {
  listEstagios: (filters?: EstagioFilters) => fetchAll<Estagio, EstagioFilters>("/estagio", filters),
  buscarDadosEstagio: async (estagioId: number): Promise<any> => {
    const response = await api.get(`/termo-rescisao-pdf/${estagioId}/dados`);
    return response.data;
  },
  gerarRescisaoPdf: async (estagioId: number, dataRescisao: string, motivoPrincipal?: string): Promise<Blob> => {
    try {
      const response: AxiosResponse<Blob> = await api.post(
        `/termo-rescisao-pdf/${estagioId}/download`,
        { dataRescisao, motivoPrincipal },
        { responseType: "blob" }
      );
      return response.data;
    } catch (error: any) {
      await normalizeBlobError(error);
      throw error;
    }
  },
};

export const statsService = {
  getDashboardStats: async () => {
    try {
      const [alunos, empresas, estagiosAtivos, estagiosConcluidos, estagiosCancelados, instituicoes, cursos, cidades, seguradoras, relatorios] =
        await Promise.all([
          alunoService.list({ page: 1, limit: 1 }),
          empresaService.list({ page: 1, limit: 1 }),
          estagioService.list({ page: 1, limit: 1, status: "Ativo" }),
          estagioService.list({ page: 1, limit: 1, status: "Concluido" }),
          estagioService.list({ page: 1, limit: 1, status: "Cancelado" }),
          instituicaoService.list({ page: 1, limit: 1 }),
          cursoService.list({ page: 1, limit: 1 }),
          cidadeService.list({ page: 1, limit: 1 }),
          seguradoraService.list({ page: 1, limit: 1 }),
          relatorioEstagioService.list({ page: 1, limit: 1 }),
        ]);

      return {
        totalAlunos: alunos?.pagination?.total || 0,
        totalEmpresas: empresas?.pagination?.total || 0,
        totalEstagios: estagiosAtivos?.pagination?.total || 0,
        totalEstagiosConcluidos: estagiosConcluidos?.pagination?.total || 0,
        totalEstagiosCancelados: estagiosCancelados?.pagination?.total || 0,
        totalInstituicoes: instituicoes?.pagination?.total || 0,
        totalCursos: cursos?.pagination?.total || 0,
        totalCidades: cidades?.pagination?.total || 0,
        totalSeguradoras: seguradoras?.pagination?.total || 0,
        totalRelatorios: relatorios?.pagination?.total || 0,
      };
    } catch (error) {
      console.error("Erro ao buscar estatísticas:", error);
      return {
        totalAlunos: 0,
        totalEmpresas: 0,
        totalEstagios: 0,
        totalEstagiosConcluidos: 0,
        totalEstagiosCancelados: 0,
        totalInstituicoes: 0,
        totalCursos: 0,
        totalCidades: 0,
        totalSeguradoras: 0,
        totalRelatorios: 0,
      };
    }
  },

  getEstagiosAtivos: async () => {
    try {
      return await estagioService.getByStatus("Ativo");
    } catch (error) {
      console.error("Erro ao buscar estágios ativos:", error);
      return [];
    }
  },

  getEstagiosRecentes: async () => {
    try {
      const response = await estagioService.list({ page: 1, limit: 5 });
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar estágios recentes:", error);
      return [];
    }
  },
};
